/**
 * Words and numbers for the by-file findings view (release 2.1 Gate A items
 * 5-9, 11). Pure functions, so every phrase the reviewer reads is tested.
 */

import type { DiscoveryFileGroup } from '@/lib/api';

/**
 * Put the scanned folder into an endpoint finding's location.
 *
 * Laptop agents send `endpoint://<device>/<path under the scanned folder>`
 * and `metadata.scan_root` with the folder's own name. Joined, the reviewer
 * reads `endpoint://<device>/Downloads/User Details.xlsx` (pilot feedback,
 * Sep 2026). Agents from release 2.1 also send the full local path, which the
 * console shows first; this is the fallback for findings without it.
 */
export function withScanRoot(ref: string, scanRoot: unknown): string {
  if (typeof scanRoot !== 'string' || scanRoot.length === 0) return ref;
  const prefix = 'endpoint://';
  if (!ref.startsWith(prefix)) return ref;
  const rest = ref.slice(prefix.length);
  const slash = rest.indexOf('/');
  if (slash < 0) return ref;
  const device = rest.slice(0, slash);
  const rel = rest.slice(slash + 1);
  if (rel === scanRoot || rel.startsWith(`${scanRoot}/`)) return ref;
  return `${prefix}${device}/${scanRoot}/${rel}`;
}

const INDIAN = new Intl.NumberFormat('en-IN');

/**
 * An estimated count as a person would say it: exact below 20, otherwise two
 * significant figures after "about" ("about 980", "about 1,000",
 * "about 12,00,000" in Indian grouping).
 */
export function formatEstimate(n: number | null | undefined): string | null {
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return null;
  if (n === 0) return 'none';
  if (n < 20) return INDIAN.format(Math.round(n));
  const digits = Math.floor(Math.log10(n)) + 1;
  const unit = 10 ** Math.max(0, digits - 2);
  return `about ${INDIAN.format(Math.round(n / unit) * unit)}`;
}

const LABELS: Record<string, string> = {
  pan: 'PAN',
  aadhaar: 'Aadhaar',
  aadhaar_vid: 'Aadhaar VID',
  email: 'Email',
  phone: 'Phone',
  credit_card: 'Card number',
  debit_card: 'Card number',
  bank_account: 'Account number',
  account_number: 'Account number',
  ifsc: 'IFSC',
  gstin: 'GSTIN',
  imei: 'IMEI',
  upi: 'UPI ID',
  upi_id: 'UPI ID',
  dob: 'Date of birth',
  date_of_birth: 'Date of birth',
  passport: 'Passport',
  voter_id: 'Voter ID',
  driving_licence: 'Driving licence',
  ip_address: 'IP address',
  name: 'Name',
  address: 'Address',
};

/** A data-type label as a reviewer reads it ("pan" -> "PAN"). */
export function dataTypeLabel(label: string): string {
  const known = LABELS[label.toLowerCase()];
  if (known) return known;
  const words = label.replace(/_/g, ' ').trim();
  return words.length === 0 ? label : words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A weak match: fewer than 1 in 10 of at least 10 sampled values, e.g. one
 * stray value in a file ("1 of 59"). PARITY: `isWeakMatch` in
 * api-controller findings-by-file.ts, which hides the same findings.
 */
export function isWeakMatch(matched: number | null | undefined, sampled: number | null | undefined): boolean {
  if (typeof matched !== 'number' || typeof sampled !== 'number') return false;
  if (!Number.isFinite(matched) || !Number.isFinite(sampled)) return false;
  return sampled >= 10 && matched < sampled * 0.1;
}

/** "98% of sampled values matched (58 of 59)"; null when the scan kept no counts. */
export function matchInWords(matched: number | null | undefined, sampled: number | null | undefined): string | null {
  if (typeof matched !== 'number' || typeof sampled !== 'number' || sampled <= 0) return null;
  const pct = Math.round((matched / sampled) * 100);
  return `${pct}% of sampled values matched (${matched} of ${sampled})`;
}

/** The last path segment, for either slash. */
export function fileNameOf(path: string): string {
  const cut = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));
  return cut >= 0 ? path.slice(cut + 1) : path;
}

/** Everything before the last path segment, for either slash. */
export function folderOf(path: string): string {
  const cut = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));
  return cut > 0 ? path.slice(0, cut) : path;
}

const DATABASE_REF = /^(postgres|pg|mysql|mssql|oracle|db2i|snowflake|bigquery|redshift|databricks|mongodb|cassandra):\/\//;

/** The row's name: the file, "<file> and N similar files", or the asset. */
export function groupTitle(g: DiscoveryFileGroup): string {
  const first = g.sampleRefs[0] ?? g.key;
  if (g.kind === 'file') {
    const name = fileNameOf(g.files === 1 && g.localPath ? g.localPath : first);
    if (g.files <= 1) return name;
    const others = g.files - 1;
    return `${name} and ${INDIAN.format(others)} similar file${others === 1 ? '' : 's'}`;
  }
  if (g.assetName) return g.assetName;
  return DATABASE_REF.test(first) ? fileNameOf(first).replace(/\.[^.]*$/, '') : fileNameOf(first);
}

/**
 * Where the row lives. One file: its full path on the laptop when the agent
 * sent it, else device + scanned folder + path. Look-alike files: their
 * folder. An asset: the system it sits in.
 */
export function groupLocation(g: DiscoveryFileGroup): string {
  const first = g.sampleRefs[0] ?? '';
  if (g.kind === 'file') {
    const full = g.localPath ?? withScanRoot(first, g.scanRoot);
    return g.files <= 1 ? full : folderOf(full);
  }
  return folderOf(first);
}

/** "1 file", "40 files", "7 columns", "3 items". */
export function groupSize(g: DiscoveryFileGroup): string {
  const n = INDIAN.format(g.files);
  if (g.kind === 'file') return `${n} file${g.files === 1 ? '' : 's'}`;
  const noun = DATABASE_REF.test(g.sampleRefs[0] ?? '') ? 'column' : 'item';
  return `${n} ${noun}${g.files === 1 ? '' : 's'}`;
}

/** Owner line: the laptop's signed-in user, said as such. */
export function groupOwner(g: DiscoveryFileGroup): string | null {
  return g.deviceUser ? `${g.deviceUser} (laptop user)` : null;
}

/** One column behind a finding, as the agent reported it. */
export interface FileColumnHit {
  sheet: string | null;
  column: string;
  matched: number;
  sampled: number;
  estimated: number;
}

/** "Customers > PAN: about 1,000 (20 of 20 sampled)". */
export function columnLine(c: FileColumnHit): string {
  const where = c.sheet ? `${c.sheet} > ${c.column}` : c.column;
  const est = formatEstimate(c.estimated) ?? String(c.estimated);
  return `${where}: ${est} (${c.matched} of ${c.sampled} sampled)`;
}

/** How an estimate was made, in one sentence. */
export function estimateBasis(columns: FileColumnHit[], rowsInFile: number | null): string {
  const rows = typeof rowsInFile === 'number' && rowsInFile > 0 ? INDIAN.format(rowsInFile) : null;
  if (columns.length > 0) {
    return rows
      ? `Estimated per column: the share of sampled values that matched, applied to the ${rows} rows the file holds.`
      : 'Estimated per column: the share of sampled values that matched, applied to the rows the file holds.';
  }
  return rows
    ? `Estimated from the lines sampled, applied to the ${rows} lines the file holds.`
    : 'Estimated from the lines sampled.';
}

/** Parse `metadata.columns` defensively: anything malformed is dropped, never thrown. */
export function parseFileColumns(v: unknown): FileColumnHit[] {
  if (!Array.isArray(v)) return [];
  const out: FileColumnHit[] = [];
  for (const raw of v) {
    if (!raw || typeof raw !== 'object') continue;
    const c = raw as Record<string, unknown>;
    if (typeof c['column'] !== 'string') continue;
    const num = (x: unknown): number => (typeof x === 'number' && Number.isFinite(x) ? x : 0);
    out.push({
      sheet: typeof c['sheet'] === 'string' && c['sheet'].length > 0 ? c['sheet'] : null,
      column: c['column'],
      matched: num(c['matched']),
      sampled: num(c['sampled']),
      estimated: num(c['estimated']),
    });
  }
  return out;
}
